// steuerUndAusweisAngaben.ts — Steuerklasse, Jahreseinkommen, Personalausweis,
// Sozialversicherungsnummer, Steuer-ID.
//
// Vokabular und Darstellung dieser Angaben, damit beide Masken (Akte und
// Anlage) sie gleich behandeln.
//
// Bis 09/2026 hieß diese Datei `geschuetzteAngaben.ts` und enthielt zusätzlich
// die Regel, WER sie erfassen darf: nur die Kontaktverantwortlichen (FO/BO)
// sowie gfgl und admin. Der PO hat diese Sonderstellung am 21.09.2026
// aufgehoben — die Angaben werden wie jede andere Kontaktangabe gelesen und
// geschrieben. Geblieben sind Vokabular und Formatierung.
//
// Rein, ohne React/DOM → in der vitest-Umgebung „node" testbar.

/** Die deutschen Lohnsteuerklassen. Geschlossenes Vokabular, kein Katalog. */
export const STEUERKLASSEN = ["I", "II", "III", "IV", "V", "VI"] as const;

export type Steuerklasse = (typeof STEUERKLASSEN)[number];

/**
 * Property-Namen dieser Angaben.
 *
 * Gebraucht wird die Liste weiterhin von den Beleg-Mappern (Ausweis-Scan,
 * Gehaltsabrechnung), die wissen müssen, welche Felder sie befüllen.
 */
export const STEUER_UND_AUSWEIS_FELDER = [
  "taxClass",
  "annualIncome",
  "idCardNumber",
  "idCardIssuedAt",
  "idCardAuthority",
  "idCardType",
  "idCardSubtype",
  "idCardClasses",
  "taxIdNumber",
  "idCardValidUntil",
  // Sozialversicherungs- UND Rentenversicherungsnummer sind dieselbe
  // zwölfstellige Nummer — ein Feld, zwei geläufige Namen.
  "socialSecurityNumber",
] as const;

/**
 * Jahreseinkommen fürs Eingabefeld: „84.000" statt „84000".
 *
 * Bewusst OHNE Nachkommastellen, solange der Betrag ganzzahlig ist. Ein
 * Jahreseinkommen wird in vollen Euro erfasst; ein automatisch angehängtes
 * „,00" verleitet dazu, es zu überschreiben, und aus „84.000,00" wird beim
 * Weitertippen schnell ein hundertfacher Betrag. Krumme Bestandswerte behalten
 * ihre zwei Stellen.
 */
export function jahreseinkommenAnzeige(wert: number | string | null | undefined): string {
  if (wert === null || wert === undefined || wert === "") return "";
  const zahl = typeof wert === "number" ? wert : Number(wert);
  if (!Number.isFinite(zahl)) return "";
  const ganz = Number.isInteger(zahl);
  return zahl.toLocaleString("de-DE", {
    minimumFractionDigits: ganz ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Eingabe → gespeicherter Wert. Streng de-DE (Punkt = Tausender, Komma =
 * Dezimal), dieselbe Festlegung wie in `utils/numberGrouping`.
 *
 * Ein geleertes Feld liefert `null`, nicht `undefined` und nicht `0`: `undefined`
 * verschwände in JSON.stringify und der alte Betrag bliebe stehen, `0` wäre eine
 * fachliche Aussage („verdient nichts"), die niemand getroffen hat.
 */
export function jahreseinkommenAusEingabe(text: string): number | null {
  const roh = (text ?? "").trim();
  if (!roh) return null;
  const bereinigt = roh.replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
  if (!bereinigt || bereinigt === "-") return null;
  const zahl = Number(bereinigt);
  return Number.isFinite(zahl) ? zahl : null;
}

/**
 * Ein geleertes `<input type="date">` liefert "" — und "" ist für eine
 * Postgres-`date`-Spalte kein Datum (22007). Die Leerung muss deshalb als
 * `null` gehen, sonst scheitert das Speichern der ganzen Maske.
 */
export function datumAusEingabe(text: string): string | null {
  const roh = (text ?? "").trim();
  return roh ? roh : null;
}

/** ISO-Datum aufs Eingabeformat kürzen (die Spalte liefert „2030-04-01"). */
export function datumFuersFeld(wert: string | null | undefined): string {
  return String(wert ?? "").slice(0, 10);
}

/** Wer schaut gerade auf die Maske? */
